import React from 'react'

import EmptyState from '@/components/base/emptyState'

import { Hourglass } from 'lucide-react'
import { ProgramPreview, isPremiumAuthor } from '@/lib/api/community'
import { cn } from '@/lib/utils'

import { AppContext } from '@/components/base/appcontext'
import CarouselRow, { CAROUSEL_CARD } from '@/components/base/carouselRow'
import LinkWithState from '@/components/base/link'
import UserAvatar from '@/components/base/userAvatar'

import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'

const TrandsPanel: React.FC<{
    items: ProgramPreview[]
    seeMoreLink?: string
}> = ({ items, seeMoreLink }) => {
    const appContext = React.useContext(AppContext)

    return (
        <CarouselRow
            empty={items.length === 0}
            emptyState={
                <EmptyState
                    variant="section"
                    kind="waiting"
                    icon={Hourglass}
                    title={appContext.geti18n('empty.trends.title')}
                    description={appContext.geti18n('trends.panel.noItems')}
                    action={{
                        label: appContext.geti18n('empty.trends.explore'),
                        to: '/communityProgrammes/list'
                    }}
                />
            }
            seeMoreLink={seeMoreLink}
        >
            {items.map((e) => (
                <li
                    key={'td-' + e.id}
                    className={cn(
                        CAROUSEL_CARD,
                        'flex items-start gap-2.5 border bg-card p-3'
                    )}
                >
                    <LinkWithState
                        to={`/coaches/${e.author}`}
                        className="shrink-0"
                    >
                        <UserAvatar
                            name={e.expand?.author?.login}
                            photo={e.expand?.author?.photo}
                            coach={isPremiumAuthor(e)}
                            className="h-9 w-9"
                            fallbackClassName="text-xs font-semibold"
                        />
                    </LinkWithState>
                    <LinkWithState
                        to={`/communityProgrammes/${e.origId}/details`}
                        className="flex-1 min-w-0"
                    >
                        <Typography
                            variant="title-sm"
                            className="leading-tight truncate"
                        >
                            {e.title}
                        </Typography>
                        <Typography
                            variant="body-xs"
                            className="leading-tight mb-1.5 truncate"
                        >
                            {appContext
                                .geti18n('trands.panel.byAuthor')
                                .replace(
                                    '{login}',
                                    e.expand?.author?.login || ''
                                )}
                        </Typography>
                        <div className="flex flex-wrap gap-1">
                            {e.tags.slice(0, 4).map((t, idx) => (
                                <Badge
                                    variant="secondary"
                                    key={idx + t}
                                    size="xs"
                                    className="border border-primary/10"
                                >
                                    {appContext.geti18n('options.text.' + t)}
                                </Badge>
                            ))}
                        </div>
                    </LinkWithState>
                </li>
            ))}
        </CarouselRow>
    )
}

export default TrandsPanel
